import {
  AppBar,
  Box,
  CssBaseline,
  Toolbar,
  IconButton
} from '@mui/material'
import { useMemo, useState } from 'react'

import { pb, usePaginatedCollection, useUser } from '@/pb'
import WorkoutView from './WorkoutView'
import AnalyticsScreen from './AnalyticsScreen'
import HomeScreen from './HomeScreen'
import StartTrainingScreen from './StartTrainingScreen'
import Account from '../Account'
import { enrichWorkout, createMusicWorkout, finishWorkout} from './util'
import WorkoutHistory from './WorkoutHistory'
import { Route, useLocation, Switch } from 'wouter'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

export default function Mobile() {
  const { user } = useUser()
  const [location, navigate] = useLocation();

  const {
    records: workoutTemplates,
    loaded: workoutTemplatesLoaded
  } = usePaginatedCollection('music_workout_templates', {
    page: 1,
    perPage: 30,
    sort: '-created',
    expand: 'musicId'
  })

  const {
    records: workoutHistory,
    loaded: workoutHistoryLoaded
  } = usePaginatedCollection('music_workouts', {
    page: 1,
    perPage: 30,
    sort: '-created',
    filter: `userId = "${user.id}"`
  })

  const workoutHistoryRecent = useMemo(() => {
    if (!workoutHistoryLoaded || !workoutTemplates) return [];
    return workoutHistory.map((workout) => enrichWorkout(workout, workoutTemplates));
  }, [workoutHistory, workoutTemplates, workoutHistoryLoaded]);


  const startTraining = async ({ workoutId, device, groupId, startTime }) => {
    if (!workoutId || !device) return;

    try {
      const newWorkoutRecord = await createMusicWorkout({
        templateId: workoutId,
        deviceId: device.id,
        userId: user.id,
        groupId,
        startTime,
      });

      navigate(`/app/workout/${newWorkoutRecord.id}`);
    } catch (error) {
      console.error("Failed to create music workout:", error);
    }
  };

  const openWorkout = (workoutId) => {
    navigate(`/app/workout/${workoutId}`);
  };

  const backButtonMap = {
    "/app/start": "/app",
    "/app/history": "/app",
    "/app/analytics": "/app",
    "/app/account": "/app",
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />
      <AppBar position="sticky" color="default" elevation={0}>
        <Toolbar sx={{ position: "relative", display: "flex", alignItems: "center", minHeight: 64, px: 2 }}>
          {/* Logic for dynamic back button based on current path */}
          {(location !== '/app' && !location.startsWith('/app/workout')) && (
            <IconButton
              onClick={() => navigate(backButtonMap[location] || '/app')}
              sx={{ position: "absolute", left: 16 }}
              edge="start"
            >
              <ArrowBackIosIcon />
            </IconButton>
          )}

          {location.startsWith('/app/workout') && (
            <IconButton
              onClick={() => navigate('/app')}
              sx={{ position: "absolute", left: 16 }}
              edge="start"
            >
              <ArrowBackIosIcon />
            </IconButton>
          )}

          <a href="/app" style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", padding: "0.5em", display: "flex", alignItems: "center" }}>
            <img src="/logo.webp" alt="Logo" style={{ maxWidth: "120px" }} />
          </a>
        </Toolbar>
      </AppBar>

      <Switch>
        <Route path="/app">
          <HomeScreen />
        </Route>

        <Route path="/app/account" component={Account} />
        <Route path="/app/analytics">
          <AnalyticsScreen workoutTemplates={workoutTemplates} />
        </Route>
        <Route path="/app/history">
          <WorkoutHistory workoutTemplates={workoutTemplates} onOpenWorkout={openWorkout} />
        </Route>

        <Route path="/app/workout/:workoutid">
          {params =>
            <WorkoutView
              workoutid={params.workoutid}
            />
          }
        </Route>

        <Route path="/app/start">
          <StartTrainingScreen
            workoutTemplates={workoutTemplates}
            workoutTemplatesLoaded={workoutTemplatesLoaded}
            onStart={startTraining}
          />
        </Route>
      </Switch>
    </Box>
  )
}